import React, { useRef } from 'react';
import { isWeb, Platform } from '../util/platform';
import { View, StyleSheet, Modal, Pressable, Share, Alert } from 'react-native'
import { Feather } from '@expo/vector-icons';
import QRCode from 'react-native-qrcode-svg';

import AppStyles from '../AppStyles';
import CustomText from './CustomText';
import { t as translate, useTranslation } from '../i18n';

const buildQRPayload = (organization, location) => {
  const params = new URLSearchParams({
    organization: organization ?? '',
    location: location ?? '',
    noLogin: 'true',
  });
  return `https://monetareport.net/submit-report?${params.toString()}`;
};

const QRCodeModal = ({
 visible, organization, location, onClose }) => {
  const { t } = useTranslation('admin');
  const svgRef = useRef(null);

  if (!visible || !location) return null;

  const payload = buildQRPayload(organization, location);

  const handleShare = async () => {
    try {
      if (isWeb()) {
        downloadQRSvg(svgRef.current, location);
        return;
      }
      await Share.share({
        message: t('qrCode.shareMessage', { location, url: payload }),
        url: payload,
        title: t('qrCode.shareTitle', { location }),
      });
    } catch (err) {
      Alert.alert(t('qrCode.shareFailed'), err?.message ?? String(err));
    }
  };

  const shareLabel = isWeb() ? t('qrCode.download') : t('common:actions.share');
  const shareIcon = isWeb() ? 'download' : 'share-2';

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable style={styles.overlay} onPress={onClose}>
        <Pressable style={styles.modal} onPress={(e) => e.stopPropagation()}>
          <CustomText
            font="semiBold"
            size={18}
            color={AppStyles.colorSet.textPrimary}
            style={styles.title}
          >
            {t('qrCode.title')}
          </CustomText>
          <CustomText
            font="regular"
            size={14}
            color={AppStyles.colorSet.textSecondary}
            style={styles.subtitle}
          >
            {location}
          </CustomText>

          <View style={styles.qrContainer}>
            <QRCode
              value={payload}
              size={220}
              backgroundColor="#FFFFFF"
              color="#000000"
              getRef={(ref) => {
                svgRef.current = ref;
              }}
            />
          </View>

          <View style={styles.buttonRow}>
            <Pressable
              style={[styles.button, styles.cancelButton]}
              onPress={onClose}
              accessibilityRole="button"
              accessibilityLabel={t('qrCode.closeLabel')}
            >
              <CustomText font="medium" size={14} color={AppStyles.colorSet.textPrimary}>
                {t('qrCode.close')}
              </CustomText>
            </Pressable>

            <Pressable
              style={[styles.button, styles.shareButton]}
              onPress={handleShare}
              accessibilityRole="button"
              accessibilityLabel={shareLabel}
            >
              <Feather name={shareIcon} size={16} color="#000000" style={{ marginRight: 6 }} />
              <CustomText font="medium" size={14} color="#000000">
                {shareLabel}
              </CustomText>
            </Pressable>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

const downloadQRSvg = (svgRef, location) => {
  if (!svgRef || typeof svgRef.toDataURL !== 'function') {
    Alert.alert(translate('admin:qrCode.downloadFailed'), translate('admin:qrCode.notReady'));
    return;
  }
  svgRef.toDataURL((base64) => {
    const href = `data:image/png;base64,${base64}`;
    const a = document.createElement('a');
    a.href = href;
    a.download = `qr-${(location ?? 'location').replace(/[^a-z0-9-_]+/gi, '_')}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  });
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modal: {
    backgroundColor: AppStyles.colorSet.whiteColor,
    borderRadius: 16,
    padding: 24,
    maxWidth: 360,
    width: '100%',
    alignItems: 'center',
    ...Platform.select({
      web: {
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.15,
        shadowRadius: 20,
        elevation: 8,
      },
    }),
  },
  title: {
    textAlign: 'center',
    marginBottom: 4,
  },
  subtitle: {
    textAlign: 'center',
    marginBottom: 20,
  },
  qrContainer: {
    padding: 16,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: AppStyles.colorSet.cardBorder,
    marginBottom: 24,
  },
  buttonRow: {
    flexDirection: 'row',
    gap: 12,
    width: '100%',
  },
  button: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
  },
  cancelButton: {
    backgroundColor: AppStyles.colorSet.inputBackground,
    borderWidth: 1,
    borderColor: AppStyles.colorSet.cardBorder,
  },
  shareButton: {
    backgroundColor: AppStyles.colorSet.brandPrimary,
  },
});

export default QRCodeModal;
